import React from "react";
import { Box, Typography, Alert } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { AssessmentResponseDto } from "../../shared/dtos/assessment/AssessmentResponseDto";
import { formatDateForDisplay } from "../../helpers/dateHelpers";

interface ViewAssessmentModalProps {
  open: boolean;
  onClose: () => void;
  assessment: AssessmentResponseDto | null;
  loading?: boolean;
  error?: string | null;
}

const ViewAssessmentModal: React.FC<ViewAssessmentModalProps> = ({
  open,
  onClose,
  assessment,
  loading = false,
  error = null,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 5,
          }}
        >
          <AppCircularProgress />
          <Typography sx={{ ml: 2, color: "text.secondary" }}>
            Loading Assessment Details...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!assessment) {
      return (
        <Typography sx={{ my: 2, color: "text.secondary" }}>
          Assessment data not available.
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <DetailItem label="Assessment Name" value={assessment.name} />
        <DetailItem
          label="Type"
          value={assessment.type?.replace(/_/g, " ") || "N/A"}
        />
        <DetailItem label="Subject" value={assessment.subjectName} />
        <DetailItem label="Classroom" value={assessment.classroomName} />
        <DetailItem label="Term" value={assessment.termName} />
        <DetailItem
          label="Date"
          value={formatDateForDisplay(assessment.assessmentDate)}
        />
        <DetailItem label="Max Marks" value={assessment.maxMarks} />
        <DetailItem
          label="Weightage"
          value={`${assessment.weightage ?? 100}%`}
        />
        <DetailItem
          label="Created By"
          value={assessment.createdByTeacherName || "Staff"}
        />
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Assessment Details"
      width="md"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewAssessmentModal;
